// ─────────────────────────────────────────────────────────────────────────────
// graphBuffers — records and positions in, typed arrays out.
//
// NO REACT COMPONENT PER NODE. NO MESH PER FILE. Every node in a chunk is one
// point in one BufferGeometry and every edge is two vertices in one
// LineSegments — which is the only reason a galaxy of this size can exist at a
// handful of draw calls.
//
// CHUNKING IS STORAGE, NOT A CAP. When a chunk fills, the next one is
// allocated. The graph is unbounded, and the things that keep it renderable are
// semantic zoom, hydration and level-of-detail — never a number that silently
// discards the rest of the system. No such number is named anywhere in this
// module, in code OR in prose: a constant that exists only in a comment is how a
// dead lever gets reintroduced by the next reader who takes the name for intent.
//
// The visibleIndex → node id mapping travels WITH the chunk. Picking (G1) reads
// it; nothing has to reconstruct the correspondence from draw order later.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphStore } from './graphStore';
import { GraphPositions } from './graphLayout';
import { RexyGraphEdgeRecord, RexyGraphNodeRecord } from './graphTypes';
import {
  baseEdgeAlpha, edgePriority, kindIndex, relationIndex, visualForKind, RexyGraphNodeLayer,
} from './graphRegistry';
import { Rgb, minVisibleAlphaFor, nodeAlpha, nodeColour, resolveDomainColour } from './graphPalette';
import { TAU, hashUnit } from './graphMath';

/** One chunk's worth of points. Not a limit — see the module header. */
export const GRAPH_BUFFER_CHUNK_SIZE = 65536;

export interface NodeBufferChunk {
  count: number;
  position: Float32Array; // count * 3
  aColor: Float32Array; // count * 3
  aSize: Float32Array; // count
  /**
   * INTERACTION size, in CSS pixels — deliberately NOT aSize.
   *
   * The owner's original bug lived in conflating the two. Picking used the
   * VISUAL size, so at galaxy distance a domain shrank to roughly eight pixels
   * while a two-pixel memory file was floored UP to six — which is why deep
   * nodes felt easier to hit than the hubs in front of them. A hit target is a
   * property of what a thing IS, not of how big it currently looks.
   */
  aPickSize: Float32Array; // count
  /**
   * Semantic pick priority. Where two proxies overlap, the structurally larger
   * entity wins: a domain is never stolen by a file drawn in front of it.
   */
  aPickPriority: Float32Array; // count
  aAlpha: Float32Array; // count
  /** Static render emphasis by kind/weight. Never a substitute for hierarchy. */
  aImportance: Float32Array; // count
  /** Dynamic. Written on selection/hover WITHOUT rebuilding positions. */
  aSelected: Float32Array; // count
  aHovered: Float32Array; // count
  /** Dynamic. Neighbourhood context fade, 1.0 when nothing is selected. */
  aContextAlpha: Float32Array; // count
  aPulsePhase: Float32Array; // count
  aKind: Float32Array; // count
  /** Global visible index; the picking material encodes it to RGB in G1. */
  aPickId: Float32Array; // count
  /** visibleIndex (within this chunk) → graph node id. */
  ids: string[];
}

export interface EdgeBufferChunk {
  count: number; // edges, not vertices
  position: Float32Array; // count * 6
  colour: Float32Array; // count * 6
  alpha: Float32Array; // count * 2
  relationKind: Float32Array; // count * 2
  ids: string[];
}

export interface GraphBuffers {
  hub: NodeBufferChunk[];
  micro: NodeBufferChunk[];
  edges: EdgeBufferChunk[];
  nodeCount: number;
  edgeCount: number;
  /** Radius of the sphere containing every placed node. */
  boundingRadius: number;
  /**
   * The radius containing 97% of nodes — what the camera should FRAME.
   *
   * Six orphans deliberately sit on an outer shell, and framing the true
   * bounding sphere therefore framed the orphans and left the galaxy a small
   * bright smear in the middle of the viewport. The outliers stay in the scene;
   * they just stop dictating the composition.
   */
  contentRadius: number;
  /**
   * Axis-aligned spans of the content, and the min/max ratio.
   *
   * A G1 DIAGNOSTIC, not a production rule: a ratio near 1 is a volume, a ratio
   * near zero is the slab the perturbed ring produced. Reported so "it looks
   * spherical" is a number rather than an impression.
   */
  extents: { x: number; y: number; z: number; ratio: number };
  /**
   * A digest of every node id and its position, quantised to a millimetre of
   * world space. Two runs that produce the same digest placed every node in
   * exactly the same spot — which is how "the layout is identical after reload"
   * is PROVEN across two separate processes rather than judged by eye from two
   * screenshots that look similar.
   */
  layoutDigest: string;
  /**
   * A digest of every node's rendered colour. It is SEPARATE from layoutDigest
   * on purpose: a colour re-ruled through a patch must be provable without
   * anything moving, and a node moved must be provable without anything
   * changing colour. One digest for both would not distinguish them.
   */
  colourDigest: string;
  /** Rendered edges by priority — the LOD's own accounting, 1-indexed [p1..p4]. */
  edgesByPriority: [number, number, number, number];
}

function allocNodeChunk(count: number): NodeBufferChunk {
  return {
    count,
    position: new Float32Array(count * 3),
    aColor: new Float32Array(count * 3),
    aSize: new Float32Array(count),
    aPickSize: new Float32Array(count),
    aPickPriority: new Float32Array(count),
    aAlpha: new Float32Array(count),
    aImportance: new Float32Array(count),
    aSelected: new Float32Array(count),
    aHovered: new Float32Array(count),
    aContextAlpha: new Float32Array(count).fill(1),
    aPulsePhase: new Float32Array(count),
    aKind: new Float32Array(count),
    aPickId: new Float32Array(count),
    ids: new Array(count),
  };
}

function allocEdgeChunk(count: number): EdgeBufferChunk {
  return {
    count,
    position: new Float32Array(count * 6),
    colour: new Float32Array(count * 6),
    alpha: new Float32Array(count * 2),
    relationKind: new Float32Array(count * 2),
    ids: new Array(count),
  };
}

/**
 * The domain colour a node inherits. A node's own `colour` overrides it; that is
 * the whole mechanism by which one agent can be recoloured from data alone.
 */
function domainRgbFor(node: RexyGraphNodeRecord, store: RexyGraphStore): Rgb {
  const domainNode =
    node.kind === 'domain' ? node : node.domainId ? store.getNode(node.domainId) : undefined;

  if (domainNode) return resolveDomainColour(domainNode.id, domainNode.colour).rgb;
  // No domain at all (City Hall, an orphan). Resolve by the node's own id so an
  // unresolved colour is still reported once rather than silently defaulted.
  return resolveDomainColour(node.id).rgb;
}

/** What this pass shows and how strongly. Supplied by the surface, never guessed. */
export interface GraphVisibility {
  /** false hides a node this pass — a filter, never the camera's distance. */
  node(node: RexyGraphNodeRecord): boolean;
  /** Both endpoints are already known visible when this is called. */
  edge(edge: RexyGraphEdgeRecord): boolean;
  /**
   * Semantic edge LOD. Receives the edge's precomputed priority and decides
   * whether it is rasterised at the current level and focus. Store membership is
   * unaffected — this is rendering only.
   */
  edgeLod?(edge: RexyGraphEdgeRecord, priority: number): boolean;
  /**
   * Multiplier on a node's alpha for the current semantic level. Deep structure
   * fades with distance instead of vanishing — see graphLod.
   */
  alphaScale?(node: RexyGraphNodeRecord): number;
  /**
   * The same distance-fade for edges. Without it the deep relation web stays at
   * full strength from orbit and buries the structure it hangs off.
   */
  edgeAlphaScale?(edge: RexyGraphEdgeRecord, sourceKind: string, targetKind: string): number;
}

/**
 * Build every buffer from the store and a resolved layout.
 *
 * Called on a graph patch, a semantic-level change or a filter change — never
 * per frame, and never on a slider (those are uniforms). Two passes over the
 * node set: one to count, so each chunk is allocated once at its exact size,
 * then one to fill. Counting twice is cheaper than growing a typed array.
 */
export function buildGraphBuffers(
  store: RexyGraphStore,
  positions: GraphPositions,
  visibility?: GraphVisibility,
): GraphBuffers {
  const hubIds: string[] = [];
  const microIds: string[] = [];

  store.forEachNode((node) => {
    if (node.hidden) return;
    if (!positions.has(node.id)) return;
    if (visibility && !visibility.node(node)) return;
    const layer: RexyGraphNodeLayer = visualForKind(node.kind).layer;
    if (layer === 'hub') hubIds.push(node.id);
    else microIds.push(node.id);
  });

  // Sorted so the buffer order — and therefore every pick id — is reproducible
  // across reloads and across machines. Map iteration order is insertion order,
  // and insertion order is whatever the patches happened to do.
  hubIds.sort();
  microIds.sort();

  let boundingRadiusSq = 0;
  let pickCursor = 0;
  const radii: number[] = [];
  let minX = Infinity; let maxX = -Infinity;
  let minY = Infinity; let maxY = -Infinity;
  let minZ = Infinity; let maxZ = -Infinity;

  // FNV-1a over every PLACED node id and its quantised position, from a sorted
  // list — so the digest depends on the graph and the layout, never on the order
  // patches arrived in.
  //
  // IT IS COMPUTED OVER THE LAYOUT, NOT THE VISIBLE SET. A digest that moved
  // when a filter was toggled or the camera changed level would answer a
  // different question than "did every node land in the same place", which is
  // the only question the determinism proof asks.
  let digest = 0x811c9dc5;
  const feed = (s: string) => {
    for (let i = 0; i < s.length; i++) {
      digest ^= s.charCodeAt(i);
      digest = Math.imul(digest, 0x01000193) >>> 0;
    }
  };
  let colourDigest = 0x811c9dc5;
  const feedColour = (s2: string) => {
    for (let i = 0; i < s2.length; i++) {
      colourDigest ^= s2.charCodeAt(i);
      colourDigest = Math.imul(colourDigest, 0x01000193) >>> 0;
    }
  };

  const placed: string[] = [];
  positions.forEach((_p, id) => placed.push(id));
  placed.sort();
  for (let i = 0; i < placed.length; i++) {
    const p = positions.get(placed[i]);
    if (!p) continue;
    feed(placed[i]);
    feed(p[0].toFixed(3) + ',' + p[1].toFixed(3) + ',' + p[2].toFixed(3));
  }

  const fill = (ids: string[]): NodeBufferChunk[] => {
    const chunks: NodeBufferChunk[] = [];
    let cursor = 0;

    while (cursor < ids.length) {
      const size = Math.min(GRAPH_BUFFER_CHUNK_SIZE, ids.length - cursor);
      const chunk = allocNodeChunk(size);

      for (let i = 0; i < size; i++) {
        const id = ids[cursor + i];
        const node = store.getNode(id);
        const p = positions.get(id);
        if (!node || !p) continue;

        const visual = visualForKind(node.kind);
        const rgb = nodeColour(visual.colourRole, domainRgbFor(node, store), node.colour);

        chunk.position[i * 3] = p[0];
        chunk.position[i * 3 + 1] = p[1];
        chunk.position[i * 3 + 2] = p[2];

        chunk.aColor[i * 3] = rgb[0];
        chunk.aColor[i * 3 + 1] = rgb[1];
        chunk.aColor[i * 3 + 2] = rgb[2];

        // `importance` scales a node UP from its kind's base size; it never
        // shrinks it below the kind, so the hierarchy stays readable.
        // Importance is a small emphasis in the shader, NOT a size multiplier —
        // inflating a point to make it matter is how a hierarchy stops reading.
        const importance = typeof node.importance === 'number' ? node.importance : 0;
        chunk.aSize[i] = visual.size;
        // Hit geometry comes from the canonical adapter's per-kind table. A node
        // the adapter did not size falls back to its visual size so it stays
        // hittable — never to zero, which would make it unclickable in silence.
        const nodeMeta = (node.metadata || {}) as { [k: string]: unknown };
        const pickPx = typeof nodeMeta.pickDiameterPx === 'number'
          ? (nodeMeta.pickDiameterPx as number) : 0;
        chunk.aPickSize[i] = pickPx > 0 ? pickPx : Math.max(visual.size, 10);
        // Priority is the INVERSE of semantic level: level 0 (domain, agent,
        // team) outranks level 3-4 (files, records) at the same pixel.
        const lvl = typeof nodeMeta.semanticLevel === 'number'
          ? (nodeMeta.semanticLevel as number) : 2;
        chunk.aPickPriority[i] = 4 - lvl;
        chunk.aImportance[i] = importance;

        // role × trust × semantic level, then the floor for its kind. The floor
        // applies ONLY to a node the filters kept: it lifts something too dim to
        // find, and never resurrects something deliberately hidden.
        const computed =
          nodeAlpha(visual.colourRole, node.trustTier) *
          (visibility && visibility.alphaScale ? visibility.alphaScale(node) : 1);
        chunk.aAlpha[i] = Math.max(computed, minVisibleAlphaFor(node.kind));

        chunk.aSelected[i] = 0;
        chunk.aHovered[i] = 0;
        chunk.aContextAlpha[i] = 1;
        chunk.aPulsePhase[i] = hashUnit(id, 'pulse') * TAU;
        chunk.aKind[i] = kindIndex(node.kind);
        chunk.aPickId[i] = pickCursor++;
        chunk.ids[i] = id;

        feedColour(id);
        feedColour(rgb[0].toFixed(4) + ',' + rgb[1].toFixed(4) + ',' + rgb[2].toFixed(4));

        const d2 = p[0] * p[0] + p[1] * p[1] + p[2] * p[2];
        if (d2 > boundingRadiusSq) boundingRadiusSq = d2;
        radii.push(Math.sqrt(d2));
        if (!node.orphan) {
          if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
          if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1];
          if (p[2] < minZ) minZ = p[2]; if (p[2] > maxZ) maxZ = p[2];
        }

      }

      chunks.push(chunk);
      cursor += size;
    }
    return chunks;
  };

  const hub = fill(hubIds);
  const micro = fill(microIds);

  // ── edges ───────────────────────────────────────────────────────────────────
  // An edge with a missing endpoint is dropped, not invented. The store already
  // removes edges when a node goes; this is the second line of that defence for
  // a source that hands us a dangling reference.
  // A hidden endpoint hides the edge. An edge that outlived the node it points
  // at would be a line drawn to a place with nothing in it.
  const visibleNodes: { [id: string]: true } = {};
  for (let i = 0; i < hubIds.length; i++) visibleNodes[hubIds[i]] = true;
  for (let i = 0; i < microIds.length; i++) visibleNodes[microIds[i]] = true;

  const edgeIds: string[] = [];
  store.forEachEdge((edge) => {
    if (edge.hidden) return;
    if (!positions.has(edge.sourceId) || !positions.has(edge.targetId)) return;
    if (!visibleNodes[edge.sourceId] || !visibleNodes[edge.targetId]) return;
    if (visibility && !visibility.edge(edge)) return;
    if (visibility && visibility.edgeLod) {
      const sn = store.getNode(edge.sourceId);
      const tn = store.getNode(edge.targetId);
      if (!sn || !tn) return;
      if (!visibility.edgeLod(edge, edgePriority(sn.kind, tn.kind, edge))) return;
    }
    edgeIds.push(edge.id);
  });
  edgeIds.sort();

  const edges: EdgeBufferChunk[] = [];
  const priorityCounts: [number, number, number, number] = [0, 0, 0, 0];
  let eCursor = 0;
  while (eCursor < edgeIds.length) {
    const size = Math.min(GRAPH_BUFFER_CHUNK_SIZE, edgeIds.length - eCursor);
    const chunk = allocEdgeChunk(size);

    for (let i = 0; i < size; i++) {
      const id = edgeIds[eCursor + i];
      const edge = store.getEdge(id);
      if (!edge) continue;
      const source = store.getNode(edge.sourceId);
      const target = store.getNode(edge.targetId);
      const a = positions.get(edge.sourceId);
      const b = positions.get(edge.targetId);
      if (!source || !target || !a || !b) continue;

      chunk.position[i * 6] = a[0];
      chunk.position[i * 6 + 1] = a[1];
      chunk.position[i * 6 + 2] = a[2];
      chunk.position[i * 6 + 3] = b[0];
      chunk.position[i * 6 + 4] = b[1];
      chunk.position[i * 6 + 5] = b[2];

      // Each end wears its own end's colour; the line interpolates between them
      // across the segment. A cross-domain link therefore reads as a gradient
      // from one borough's colour into the other's — which is the relationship,
      // drawn. Red is never chosen here merely because a link crosses a domain.
      const sourceRgb = edge.colour
        ? nodeColour('domain', domainRgbFor(source, store), edge.colour)
        : nodeColour('domain', domainRgbFor(source, store));
      const targetRgb = edge.colour
        ? nodeColour('domain', domainRgbFor(target, store), edge.colour)
        : nodeColour('domain', domainRgbFor(target, store));

      chunk.colour[i * 6] = sourceRgb[0];
      chunk.colour[i * 6 + 1] = sourceRgb[1];
      chunk.colour[i * 6 + 2] = sourceRgb[2];
      chunk.colour[i * 6 + 3] = targetRgb[0];
      chunk.colour[i * 6 + 4] = targetRgb[1];
      chunk.colour[i * 6 + 5] = targetRgb[2];

      const alpha =
        baseEdgeAlpha(source.kind, target.kind, edge, hashUnit(id, 'edge-alpha')) *
        (visibility && visibility.edgeAlphaScale
          ? visibility.edgeAlphaScale(edge, source.kind, target.kind)
          : 1);
      chunk.alpha[i * 2] = alpha;
      chunk.alpha[i * 2 + 1] = alpha;

      priorityCounts[edgePriority(source.kind, target.kind, edge) - 1]++;
      const rk = relationIndex(edge.kind);
      chunk.relationKind[i * 2] = rk;
      chunk.relationKind[i * 2 + 1] = rk;

      chunk.ids[i] = id;
    }

    edges.push(chunk);
    eCursor += size;
  }

  return {
    hub,
    micro,
    edges,
    nodeCount: hubIds.length + microIds.length,
    edgeCount: edgeIds.length,
    boundingRadius: Math.sqrt(boundingRadiusSq),
    extents: (() => {
      const x = isFinite(maxX - minX) ? maxX - minX : 0;
      const y = isFinite(maxY - minY) ? maxY - minY : 0;
      const z = isFinite(maxZ - minZ) ? maxZ - minZ : 0;
      const largest = Math.max(x, y, z);
      return { x, y, z, ratio: largest > 0 ? Math.min(x, y, z) / largest : 0 };
    })(),
    contentRadius: (() => {
      if (!radii.length) return Math.sqrt(boundingRadiusSq);
      radii.sort((a, b) => a - b);
      return radii[Math.min(radii.length - 1, Math.floor(radii.length * 0.97))];
    })(),
    layoutDigest: (digest >>> 0).toString(16),
    colourDigest: (colourDigest >>> 0).toString(16),
    edgesByPriority: priorityCounts,
  };
}
